/**
 * MatrixGraph.tsx - Matrix/grid-based graph visualization
 * 
 * CHANGE HISTORY:
 * - 251105_1845 danielbeer: Added row/column highlighting and enhanced hover interactions
 * - 251031_2050 danielbeer: Initial creation
 * 
 * Grid-based view showing all relationships in a matrix format.
 * Best for: Spotting patterns, finding clusters, comparing all relationships
 */

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import type { GraphNode, GraphEdge, GraphOptions, GraphCallbacks, GraphStyle, MatrixLayoutOptions } from './types';

export interface MatrixGraphProps<T = any> extends GraphOptions, GraphCallbacks<T> {
  data: {
    nodes: GraphNode<T>[];
    edges: GraphEdge[];
  };
  style?: GraphStyle;
  matrixOptions?: MatrixLayoutOptions;
  className?: string;
}

export const MatrixGraph = <T = any,>({
  data,
  width = 1200,
  height = 800,
  style = {},
  matrixOptions: matrixOptionsProp = {},
  onNodeClick,
  onEdgeClick,
  onEdgeHover,
  className = '',
}: MatrixGraphProps<T>) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);

  useEffect(() => {
    if (!svgRef.current || data.nodes.length === 0) return;

    d3.select(svgRef.current).selectAll('*').remove();
    const svg = d3.select(svgRef.current);
    const matrixOptions = { cellSize: 30, showLabels: true, sortBy: 'name' as const, ...matrixOptionsProp };

    const margin = { top: 150, right: 50, bottom: 50, left: 150 };
    const container = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Sort nodes
    const sortedNodes = [...data.nodes].sort((a, b) => {
      if (matrixOptions.sortBy === 'name') return a.label.localeCompare(b.label);
      if (matrixOptions.sortBy === 'group' && a.group && b.group) return a.group.localeCompare(b.group);
      return 0;
    });

    // Create edge lookup
    const edgeMap = new Map<string, GraphEdge>();
    data.edges.forEach(edge => {
      edgeMap.set(`${edge.source}-${edge.target}`, edge);
    });

    const cellSize = matrixOptions.cellSize;
    const n = sortedNodes.length;

    // Draw grid cells
    sortedNodes.forEach((sourceNode, i) => {
      sortedNodes.forEach((targetNode, j) => {
        const edge = edgeMap.get(`${sourceNode.id}-${targetNode.id}`);
        const hasEdge = !!edge;

        const fillColor = hasEdge 
          ? (typeof style.edgeColor === 'function' && edge ? style.edgeColor(edge) : edge?.color || (typeof style.edgeColor === 'string' ? style.edgeColor : '#3b82f6'))
          : '#f3f4f6';

        const isHighlighted = hoveredCell && (hoveredCell.row === i || hoveredCell.col === j);
        const isHovered = hoveredCell && hoveredCell.row === i && hoveredCell.col === j;

        const cell = container.append('rect')
          .attr('x', j * cellSize)
          .attr('y', i * cellSize)
          .attr('width', cellSize)
          .attr('height', cellSize)
          .attr('fill', fillColor)
          .attr('stroke', () => {
            if (isHovered) return '#fbbf24';
            if (isHighlighted) return '#60a5fa';
            return '#fff';
          })
          .attr('stroke-width', () => {
            if (isHovered) return 3;
            if (isHighlighted) return 2;
            return 1;
          })
          .attr('opacity', () => {
            if (isHovered) return 1;
            if (isHighlighted) return hasEdge ? 0.9 : 0.4;
            return hasEdge ? 0.8 : 0.3;
          })
          .style('cursor', hasEdge ? 'pointer' : 'default');

        // Add interactions
        cell.on('mouseenter', function(event) {
          setHoveredCell({ row: i, col: j });
          if (hasEdge && edge && onEdgeHover) {
            onEdgeHover(edge, event);
          }
        }).on('mouseleave', function(event) {
          setHoveredCell(null);
          if (onEdgeHover) {
            onEdgeHover(null, event);
          }
        });

        if (hasEdge && edge && onEdgeClick) {
          cell.on('click', (event) => onEdgeClick(edge, event));
        }
      });
    });

    // Draw row labels (source nodes)
    if (matrixOptions.showLabels) {
      sortedNodes.forEach((node, i) => {
        container.append('text')
          .attr('x', -10)
          .attr('y', i * cellSize + cellSize / 2)
          .attr('text-anchor', 'end')
          .attr('dominant-baseline', 'middle')
          .attr('font-size', '11px')
          .attr('fill', '#374151')
          .text(node.label)
          .style('cursor', onNodeClick ? 'pointer' : 'default')
          .on('click', (event) => onNodeClick && onNodeClick(node, event));
      });

      // Draw column labels (target nodes)
      sortedNodes.forEach((node, j) => {
        container.append('text')
          .attr('x', j * cellSize + cellSize / 2)
          .attr('y', -10)
          .attr('text-anchor', 'start')
          .attr('transform', `rotate(-45, ${j * cellSize + cellSize / 2}, -10)`)
          .attr('font-size', '11px')
          .attr('fill', '#374151')
          .text(node.label)
          .style('cursor', onNodeClick ? 'pointer' : 'default')
          .on('click', (event) => onNodeClick && onNodeClick(node, event));
      });
    }

    // Add legend
    const legend = container.append('g')
      .attr('transform', `translate(${n * cellSize + 20}, 0)`);

    const legendColor = typeof style.edgeColor === 'string' ? style.edgeColor : '#3b82f6';
    
    legend.append('rect')
      .attr('width', 20)
      .attr('height', 20)
      .attr('fill', legendColor)
      .attr('opacity', 0.8);

    legend.append('text')
      .attr('x', 25)
      .attr('y', 15)
      .attr('font-size', '12px')
      .attr('fill', '#374151')
      .text('Has dependency');

    legend.append('rect')
      .attr('y', 30)
      .attr('width', 20)
      .attr('height', 20)
      .attr('fill', '#f3f4f6')
      .attr('opacity', 0.3);

    legend.append('text')
      .attr('x', 25)
      .attr('y', 45)
      .attr('font-size', '12px')
      .attr('fill', '#374151')
      .text('No dependency');

  }, [data, width, height, style, matrixOptionsProp, onNodeClick, onEdgeClick, onEdgeHover, hoveredCell]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className={`matrix-graph ${className}`}
      style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        background: style.backgroundColor || '#fafafa',
      }}
    />
  );
};

export default MatrixGraph;
